import { useLoaderData } from '@remix-run/react'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import PlentyBody from '#app/components/typography/plenty-body'
import { Icon } from '#app/components/ui/icon'
import { FREE_SHIPPING_LIMIT_IN_PRICE } from '#app/constants/shipping-rates-price'
import ChooseStore from '#app/features/pdp/components/click-and-collect/choose-store'
import { type loader } from '#app/routes/_public+/product+/$styleNumber'
import { type PlentyVariantAvailability } from '#types/product'
import { getSizeOptions } from '../utils'
import ChooseStoreMobileModal from './click-and-collect/choose-store-mobile-modal'

const ProductPerksAndAvailability = ({
	firstAvailability,
	selectedSku,
	setSelectSizeOpen,
}: {
	firstAvailability: PlentyVariantAvailability[]
	selectedSku: string | null | undefined
	setSelectSizeOpen: React.Dispatch<React.SetStateAction<boolean>>
}) => {
	const { t } = useTranslation('product_details_page')
	const loaderData = useLoaderData<typeof loader>()
	const sheetTriggerRef = useRef<HTMLButtonElement | null>(null)
	const [isSheetButtonClicked, setIsSheetButtonClicked] = useState(false)

	useEffect(() => {
		if (selectedSku && isSheetButtonClicked && sheetTriggerRef.current) {
			sheetTriggerRef.current.click()
			setIsSheetButtonClicked(false)
		}
	}, [isSheetButtonClicked, selectedSku])

	const handleSheetTriggerClick = (e: React.MouseEvent) => {
		if (!selectedSku) {
			e.preventDefault()
			setSelectSizeOpen(true)
			setIsSheetButtonClicked(true)
		} else {
			setIsSheetButtonClicked(false)
		}
	}

	const productData = loaderData.productData

	if (!productData) {
		return null
	}

	const activeStyle =
		productData?.styleAvailableVariants.find(
			(it: { active: any }) => it.active,
		) || productData?.styleAvailableVariants[0]

	if (!activeStyle) {
		return null
	}

	const sizeOptions = getSizeOptions(
		activeStyle,
		loaderData,
		t,
		productData.sizeSet,
	)

	return (
		<div>
			{firstAvailability && firstAvailability.length > 0 && (
				<div className="flex items-center py-1">
					<Icon name="store" size="sm" className="mr-2" />
					<PlentyBody size="md" className="mr-1">
						{t('in_stock_in_stores', {
							count: firstAvailability.length,
						})}
					</PlentyBody>
					<div className="block md:hidden">
						<ChooseStoreMobileModal
							firstAvailability={firstAvailability}
							selectedSku={selectedSku}
							sizeOptions={sizeOptions}
						/>
					</div>
					<div className="hidden md:block">
						<ChooseStore
							firstAvailability={firstAvailability}
							triggerLabel={t('see_which_ones')}
							sheetTitle={t('click_and_collect_in_store')}
							triggerClassName="underline"
							triggerOnClick={handleSheetTriggerClick}
							sheetTriggerRef={sheetTriggerRef}
							selectedSku={selectedSku}
						/>
					</div>
				</div>
			)}
			<div className="hidden py-1 md:flex">
				<Icon name="safe-lock" size="sm" className="mr-2" />
				<PlentyBody size="md">{t('safe_payment')}</PlentyBody>
			</div>
			<div className="flex items-center py-1">
				<Icon name="info-circle" size="sm" className="mr-2" />
				<PlentyBody size="md">{t('click_and_collect_label')}</PlentyBody>
			</div>
			<div className="hidden py-1 md:flex">
				<Icon name="delivery-truck" size="sm" className="mr-2" />
				<PlentyBody size="md">
					{t('free_shipping', {
						'free-delivery': FREE_SHIPPING_LIMIT_IN_PRICE,
					})}
				</PlentyBody>
			</div>
		</div>
	)
}

export default ProductPerksAndAvailability
